import React from 'react';
import dayjs from 'dayjs';
import { getPrivateFilePreview } from 'api/ehr';
import { npiIssue, npiIssueBlocksReview, npiIssueText } from 'utils/npiCheck';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import PdfIcon from 'components/form/FileUploadPrivate/pdf.png';
import type { CredStepKey } from './steps';
import { AllState } from 'constants/common';
import { getProviderPriorCredentialing } from 'api/credentialing';
import {
    DISCLOSURES,
    TAX_ID_ERROR,
    SPECIALTY_OTHERS,
    MONTHLY_VISITS_OPTIONS,
    PATIENT_PAYER_MIX_OPTIONS,
    locationCareDeliveryLabel,
} from './constants';
import { useCredTargetProvider } from './steps/targetProvider';
import MalpracticeExpiryNotice from './components/MalpracticeExpiryNotice';
import { caqhStatusLine } from './components/CaqhCheck';
import ExternalLinkIcon from 'components/MenuIcons/ExternalLinkIcon';
import s from './s.module.less';

export interface ReviewSectionMeta {
    key: CredStepKey;
    title: string;
    stepIndex: number;
}

export const REVIEW_SECTIONS: ReviewSectionMeta[] = [
    { key: 'identity', title: 'Basic information', stepIndex: 0 },
    { key: 'licenses', title: 'Licenses', stepIndex: 1 },
    { key: 'practice', title: 'Practice', stepIndex: 2 },
    { key: 'malpractice', title: 'Malpractice', stepIndex: 3 },
    { key: 'experience', title: 'Background', stepIndex: 4 },
    { key: 'references', title: 'References', stepIndex: 5 },
    { key: 'final', title: 'Verification', stepIndex: 6 },
];

type Values = Record<string, any>;

// Step 3 的执业地址数组字段名（一个州一条）
const ADDR = 'addressList';
const stateName = (code?: string) => AllState.find((i) => i.dictKey === code)?.content || code || '';

const isBlank = (v: any) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

export type NamePath = (string | number)[];

const getByPath = (v: Values, name: NamePath | null) =>
    name == null ? undefined : name.reduce<any>((acc, k) => (acc == null ? acc : acc[k]), v);

// 选填条目是否真的填了内容：表单默认塞一条空行 [{}]，id / licenseType 不算用户填写的内容。
// 提交时空行会被 credReviewData 的 isBlankLicenseEntry 过滤掉，所以校验上也一律当作「没填」。
const hasEntryValue = (o: any) =>
    !!o &&
    Object.entries(o).some(
        ([k, val]) =>
            k !== 'id' && k !== 'licenseType' && val != null && val !== '' && !(Array.isArray(val) && !val.length),
    );

// ---------- 格式/合理性校验辅助 ----------
const digits = (v: any) => String(v ?? '').replace(/\D/g, '');
// 电话展示格式化：9085555501 -> (908) 555-5501
const fmtPhone = (v: any) => {
    const d = digits(v).slice(0, 10);
    if (!d) return v ?? '';
    if (d.length < 4) return `(${d}`;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const toDay = (v: any) => (isBlank(v) ? null : dayjs(v));
const validZip = (v: any) => /^\d{5}(-\d{4})?$/.test(String(v ?? '').trim());
// Tax ID 既可能是 EIN（XX-XXXXXXX），也可能是个人 SSN（XXX-XX-XXXX，sole proprietor 常用）：
// 分隔符位置不固定，只校验去掉非数字后是 9 位
const validTaxId = (v: any) => digits(v).length === 9;
const validEmail = (v: any) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v ?? ''));
// DOB 为 MM/DD/YYYY 字符串：需为合法日历日、非未来、年份 >= 1900
const validDob = (v: any) => {
    const m = String(v ?? '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const d = dayjs(`${m[3]}-${m[1]}-${m[2]}`);
    return d.isValid() && d.format('MM/DD/YYYY') === v && !d.isAfter(dayjs(), 'day') && d.year() >= 1900;
};

interface InvalidField {
    name: NamePath;
    error: string;
    // NpiRegistryBox 已在字段下方展示同义错误：setFields 标红时跳过（见 fieldMarkableErrors），
    // 但保存/提交的拦截判断仍要把它算在内
    inlineShown?: boolean;
}
// present-but-invalid：字段有值但格式/日期不合理，在 Review 处一并拦截并标红
const INVALID: Partial<Record<CredStepKey, (v: Values) => InvalidField[]>> = {
    identity: (v) => {
        const out: InvalidField[] = [];
        // NPI 校验（位数 + 必须是个人 NPI + 登记姓名与所填姓名一致）统一走 npiIssue，与字段下方
        // NpiRegistryBox 的内联红字同一口径同一文案。查询态由组件写进表单，'unchecked'（section 没
        // 展开过、组件从未挂载）不算错——否则每个没展开的 section 都会先误报一次
        const npiState = npiIssue(v, {
            expectType: 'Type 1',
            expectedName: { firstName: v.firstName, lastName: v.lastName },
        });
        if (npiIssueBlocksReview(npiState))
            out.push({ name: ['npi'], error: npiIssueText(npiState, 'Type 1'), inlineShown: true });
        if (!isBlank(v.birthDate) && !validDob(v.birthDate))
            out.push({ name: ['birthDate'], error: 'Enter a valid date of birth (not in the future)' });
        if (!isBlank(v.tel) && digits(v.tel).length !== 10)
            out.push({ name: ['tel'], error: 'Phone must be 10 digits' });
        if (!isBlank(v.fax) && digits(v.fax).length !== 10) out.push({ name: ['fax'], error: 'Fax must be 10 digits' });
        const c = v.contactAddress || {};
        if (!isBlank(c.practiceZip) && !validZip(c.practiceZip))
            out.push({ name: ['contactAddress', 'practiceZip'], error: 'ZIP must be 5 digits' });
        const m = v.mailingAddress || {};
        if (!isBlank(m.practiceZip) && !validZip(m.practiceZip))
            out.push({ name: ['mailingAddress', 'practiceZip'], error: 'ZIP must be 5 digits' });
        return out;
    },
    licenses: (v) => {
        const out: InvalidField[] = [];
        (v.practiceLicenses || []).forEach((lic: any, i: number) => {
            const iss = toDay(lic?.effectiveDate);
            const exp = toDay(lic?.expireDate);
            if (iss && iss.isAfter(dayjs(), 'day'))
                out.push({
                    name: ['practiceLicenses', i, 'effectiveDate'],
                    error: 'Issue date cannot be in the future',
                });
            if (iss && exp && !exp.isAfter(iss, 'day'))
                out.push({
                    name: ['practiceLicenses', i, 'expireDate'],
                    error: 'Expiration must be after the issue date',
                });
            else if (exp && exp.isBefore(dayjs(), 'day'))
                out.push({ name: ['practiceLicenses', i, 'expireDate'], error: 'License has expired' });
        });
        (v.boardCertifications || []).forEach((b: any, i: number) => {
            const exp = toDay(b?.expireDate);
            if (b?.boardBody && exp && exp.isBefore(dayjs(), 'day'))
                out.push({ name: ['boardCertifications', i, 'expireDate'], error: 'Certification has expired' });
        });
        return out;
    },
    practice: (v) => {
        const out: InvalidField[] = [];
        if (!isBlank(v.practicePhone) && digits(v.practicePhone).length !== 10)
            out.push({ name: ['practicePhone'], error: 'Phone must be 10 digits' });
        ((v[ADDR] as any[]) || []).forEach((a, i) => {
            if (!isBlank(a?.practiceZip) && !validZip(a.practiceZip))
                out.push({ name: [ADDR, i, 'practiceZip'], error: 'ZIP must be 5 digits' });
        });
        return out;
    },
    malpractice: (v) => {
        const out: InvalidField[] = [];
        // 允许未来的生效日（保单可提前生效），不校验 effective date 是否在未来
        const eff = toDay(v.effectiveDate);
        const exp = toDay(v.expireDate);
        if (eff && exp && !exp.isAfter(eff, 'day'))
            out.push({ name: ['expireDate'], error: 'Expiration must be after the effective date' });
        else if (exp && exp.isBefore(dayjs(), 'day')) out.push({ name: ['expireDate'], error: 'Policy has expired' });
        return out;
    },
    references: (v) => {
        const out: InvalidField[] = [];
        (v.references || []).forEach((r: any, i: number) => {
            if (!isBlank(r?.phone) && digits(r.phone).length !== 10)
                out.push({ name: ['references', i, 'phone'], error: 'Phone must be 10 digits' });
            if (!isBlank(r?.email) && !validEmail(r.email))
                out.push({ name: ['references', i, 'email'], error: 'Enter a valid email' });
        });
        return out;
    },
    final: (v) => {
        const out: InvalidField[] = [];
        // 没有 CAQH 账户不拦截提交（文案承诺可先提交、之后再建），Review 里只做提醒，见 renderFinal
        if (!isBlank(v.ssn) && digits(v.ssn).length !== 9) out.push({ name: ['ssn'], error: 'SSN must be 9 digits' });
        if (!isBlank(v.taxId) && !validTaxId(v.taxId)) out.push({ name: ['taxId'], error: TAX_ID_ERROR });
        return out;
    },
};

const invalidFields = (values: Values, key: CredStepKey): InvalidField[] => INVALID[key]?.(values) || [];

interface RequiredField {
    label: string;
    name: NamePath | null;
}

// Practice license 必填口径（step 2 Next 与 Review 共用）：至少填一条。
// 一条都没填时把第一行的五个字段标必填（行全被删光时退化为 name:null，只能进 banner/toast 文案）；
// 已填了内容的行必须补全五项。全空行提交时会被 credReviewData 过滤掉，不会写脏数据。
const practiceLicenseRequiredFields = (v: Values): RequiredField[] => {
    const rows: any[] = v.practiceLicenses || [];
    const anyFilled = rows.some(hasEntryValue);
    if (!anyFilled && rows.length === 0) {
        return [{ label: 'At least one practice license', name: null }];
    }
    const out: RequiredField[] = [];
    rows.forEach((lic: any, i: number) => {
        if (!hasEntryValue(lic) && !(i === 0 && !anyFilled)) return;
        out.push(
            { label: `License ${i + 1} state`, name: ['practiceLicenses', i, 'state'] },
            { label: `License ${i + 1} type`, name: ['practiceLicenses', i, 'type'] },
            { label: `License ${i + 1} number`, name: ['practiceLicenses', i, 'licenseNumber'] },
            { label: `License ${i + 1} expiration date`, name: ['practiceLicenses', i, 'expireDate'] },
            { label: `License ${i + 1} copy`, name: ['practiceLicenses', i, 'url'] },
        );
    });
    return out;
};

// Board certification 整体选填，但上传了证书文件的条目三项必填（只在 Review 校验，step 2 Next 不拦）
const boardCertRequiredFields = (v: Values): RequiredField[] =>
    ((v.boardCertifications as any[]) || []).flatMap((b, i) =>
        b?.url
            ? [
                  {
                      label: `Board certification ${i + 1} certifying body`,
                      name: ['boardCertifications', i, 'boardBody'],
                  },
                  { label: `Board certification ${i + 1} number`, name: ['boardCertifications', i, 'licenseNumber'] },
                  { label: `Board certification ${i + 1} expiry`, name: ['boardCertifications', i, 'expireDate'] },
              ]
            : [],
    );

const REQUIRED: Record<CredStepKey, (v: Values) => RequiredField[]> = {
    identity: (v) => {
        const base: RequiredField[] = [
            { label: 'First name', name: ['firstName'] },
            { label: 'Last name', name: ['lastName'] },
            { label: 'Date of birth', name: ['birthDate'] },
            { label: 'Gender', name: ['gender'] },
            { label: 'Individual NPI (Type 1)', name: ['npi'] },
            { label: 'Credentials', name: ['credential'] },
            { label: 'Citizenship or nationality', name: ['citizenship'] },
            { label: 'Primary work email', name: ['email'] },
            { label: 'Mobile phone', name: ['tel'] },
            { label: 'Address', name: ['contactAddress', 'practiceAddress'] },
            { label: 'City', name: ['contactAddress', 'practiceCity'] },
            { label: 'State', name: ['contactAddress', 'practiceState'] },
            { label: 'ZIP code', name: ['contactAddress', 'practiceZip'] },
        ];
        // 非美籍时 work authorization type 必填（与分步表单的条件显示一致）
        if (v.citizenship && v.citizenship !== 'US citizen') {
            base.push({ label: 'Work authorization type', name: ['workAuthorizationType'] });
        }
        // 勾选了 Different mailing address（mailingAddress 有任一子字段）时，四个邮寄地址字段必填
        const m = v.mailingAddress || {};
        if (m.practiceAddress || m.practiceCity || m.practiceState || m.practiceZip) {
            base.push(
                { label: 'Mailing address', name: ['mailingAddress', 'practiceAddress'] },
                { label: 'Mailing city', name: ['mailingAddress', 'practiceCity'] },
                { label: 'Mailing state', name: ['mailingAddress', 'practiceState'] },
                { label: 'Mailing ZIP code', name: ['mailingAddress', 'practiceZip'] },
            );
        }
        return base;
    },
    // Issue date / board certification 已在 StepLicenses 改为选填（上传了证书文件的 board 条目除外，见 boardCertRequiredFields）。
    // Practice license 至少填一条（step 2 Next 与 Review 共用 practiceLicenseRequiredFields 这套口径）；
    // ID type / 政府 ID 只在 Review 必填，step 2 Next 不拦（见 getLicensesStepValidation）。
    licenses: (v) => [
        ...practiceLicenseRequiredFields(v),
        ...boardCertRequiredFields(v),
        { label: 'ID type', name: ['identification', 'type'] },
        { label: 'Government-issued ID', name: ['identification', 'frontUrl'] },
    ],
    // practiceType 是入口自动派生、disabled 不可编辑的字段：在 group/override 流程里 URL 无 type 参数，
    // 若列为必填会在 group review 处标红且用户无从修改（死锁）。因此不校验，只保证由 StepPractice 自动填充。
    practice: (v) => {
        const base: RequiredField[] = [
            { label: 'Clinic / Practice name', name: ['practiceName'] },
            { label: 'Practice start', name: ['startDate'] },
            { label: 'Visits billed a month', name: ['monthlyVisits'] },
            { label: 'How patients pay', name: ['patientPayerMix'] },
            { label: 'Practice phone', name: ['practicePhone'] },
            { label: 'Specialty offered', name: ['specialtyOffered'] },
            { label: 'Care delivery', name: ['careDelivery'] },
        ];
        // 每条执业地址四项地址 + 服务类型都必填；多于一条时标签带上序号便于定位
        const addrs: any[] = Array.isArray(v[ADDR]) && v[ADDR].length ? v[ADDR] : [{}];
        addrs.forEach((_a, i) => {
            const suffix = addrs.length > 1 ? ` (Address ${i + 1})` : '';
            base.push(
                { label: `Address${suffix}`, name: [ADDR, i, 'practiceAddress'] },
                { label: `City${suffix}`, name: [ADDR, i, 'practiceCity'] },
                { label: `State${suffix}`, name: [ADDR, i, 'practiceState'] },
                { label: `ZIP code${suffix}`, name: [ADDR, i, 'practiceZip'] },
                { label: `Services provided at this location${suffix}`, name: [ADDR, i, 'careDelivery'] },
            );
        });
        // 选了 Others 就必须填具体专科，否则提交时 Others 会被丢掉（等于没选）
        if ((v.specialtyOffered || []).includes(SPECIALTY_OTHERS)) {
            base.push({ label: 'Specialty offered (Others)', name: ['specialtyOfferedOther'] });
        }
        return base;
    },
    malpractice: () => [
        { label: 'Insurance carrier', name: ['carrier'] },
        { label: 'Policy / certificate number', name: ['licenseNumber'] },
        { label: 'Effective date', name: ['effectiveDate'] },
        { label: 'Expiration date', name: ['expireDate'] },
        { label: 'Per-occurrence limit', name: ['perOccurrenceCents'] },
        { label: 'Aggregate limit', name: ['aggregateCents'] },
        { label: 'Certificate copy', name: ['url'] },
    ],
    experience: () => [
        { label: 'Organization name', name: ['providerExperienceList', 0, 'organizationName'] },
        { label: 'Title', name: ['providerExperienceList', 0, 'title'] },
        { label: 'School', name: ['providerEducationList', 0, 'schoolName'] },
        { label: 'Degree', name: ['providerEducationList', 0, 'degree'] },
    ],
    references: () =>
        [0, 1, 2].flatMap((i) => [
            { label: `Reference ${i + 1} name`, name: ['references', i, 'fullName'] },
            { label: `Reference ${i + 1} phone`, name: ['references', i, 'phone'] },
            { label: `Reference ${i + 1} email`, name: ['references', i, 'email'] },
        ]),
    final: (v) => {
        const base: RequiredField[] = [{ label: 'CAQH account', name: ['hasCaqhAccount'] }];
        if (v.hasCaqhAccount === true) {
            base.push({ label: 'CAQH username', name: ['caqhUsername'] });
            base.push({ label: 'CAQH password', name: ['caqhPassword'] });
        }
        base.push(
            { label: 'Social security number', name: ['ssn'] },
            { label: 'Legal tax name', name: ['legalTaxName'] },
            { label: 'Tax ID / EIN', name: ['taxId'] },
            { label: 'Business entity type', name: ['businessEntityType'] },
            { label: 'W-9 form', name: ['w9FormUrl'] },
            // Payer participation：boolean false 不算缺失（isBlank 不含 false）
            { label: 'Admitting privileges at a facility', name: ['hasFacilityPrivileges'] },
        );
        // 有机构 admitting privileges 时机构名必填
        if (v.hasFacilityPrivileges === true) {
            base.push({ label: 'Facility name', name: ['facilityName'] });
        }
        const disclosures: any[] = v.disclosures || [];
        DISCLOSURES.forEach((d, i) => {
            const ans = disclosures[i]?.answer;
            if (ans === undefined || ans === null) {
                base.push({ label: `Disclosure "${d.q}"`, name: null });
            } else if (ans === true && isBlank(disclosures[i]?.explanation)) {
                base.push({ label: `Disclosure details for "${d.q}"`, name: null });
            }
        });
        return base;
    },
};

const missingFields = (values: Values, key: CredStepKey) =>
    REQUIRED[key](values).filter((f) => isBlank(getByPath(values, f.name)));

// 错误横幅用：缺失项 "{label} not provided" + 格式/日期不合理项的具体文案
export function getSectionErrors(values: Values, key: CredStepKey): string[] {
    return [
        ...missingFields(values, key).map((f) => `${f.label} not provided`),
        ...invalidFields(values, key).map((f) => f.error),
    ];
}

// 内联标红用：缺失且有对应输入项的字段 + 格式/日期不合理的字段（name + 报错文案）
export interface SectionFieldError {
    name: NamePath;
    errors: string[];
    // NpiRegistryBox 已内联展示的同义错误：拦截算数，标红跳过
    inlineShown?: boolean;
}

export function getSectionMissingFields(values: Values, key: CredStepKey): SectionFieldError[] {
    return [
        ...missingFields(values, key)
            .filter((f) => f.name != null)
            .map((f) => ({ name: f.name as NamePath, errors: [`${f.label} is a required field`] })),
        ...invalidFields(values, key).map((f) => ({ name: f.name, errors: [f.error], inlineShown: f.inlineShown })),
    ];
}

// setFields 可用的标红列表：剔除 NpiRegistryBox 已内联展示的错误，避免上下两条重复红字
export const fieldMarkableErrors = (fields: SectionFieldError[]) =>
    fields.filter((f) => !f.inlineShown).map((f) => ({ name: f.name, errors: f.errors }));

// Step 2（Licenses）Next 拦截专用：只校验 practice license 部分（至少一条、已填行补全、日期合理性），
// ID type / 政府 ID 留到 Review 再校验。fields 供 setFields 内联标红；
// noLicenseRow=true 表示 license 行全被删光，没有可标红的字段，只能 toast 提示
export function getLicensesStepValidation(values: Values): {
    fields: { name: NamePath; errors: string[] }[];
    noLicenseRow: boolean;
} {
    const required = practiceLicenseRequiredFields(values);
    return {
        fields: [
            ...required
                .filter((f) => f.name != null && isBlank(getByPath(values, f.name)))
                .map((f) => ({ name: f.name as NamePath, errors: [`${f.label} is a required field`] })),
            ...invalidFields(values, 'licenses')
                .filter((f) => f.name[0] === 'practiceLicenses')
                .map((f) => ({ name: f.name, errors: [f.error] })),
        ],
        noLicenseRow: required.some((f) => f.name == null),
    };
}

export function getReviewErrors(values: Values): string[] {
    return REVIEW_SECTIONS.flatMap((sec) => getSectionErrors(values, sec.key));
}

export function sectionHasErrors(values: Values, key: CredStepKey): boolean {
    return getSectionErrors(values, key).length > 0;
}

// ---------- 只读展示 ----------
const fmt = (v: any): React.ReactNode => {
    if (isBlank(v)) return null;
    if (React.isValidElement(v)) return v;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return v.filter(Boolean).join(', ') || null;
    if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
    if (typeof v === 'object' && typeof v.format === 'function') return v.format('YYYY-MM-DD');
    return String(v);
};

interface Item {
    label: string;
    value: any;
    optional?: boolean;
    // 值下方的补充说明（如 Fax 未填时提示可订阅 Klarity eFax）
    hint?: React.ReactNode;
}

const ViewItem = ({ label, value, optional, hint }: Item) => {
    const text = fmt(value);
    return (
        <div className={s.reviewItem}>
            <div className={s.reviewItemLabel}>
                {label}
                {optional && <span className={s.reviewOptTag}>Optional</span>}
            </div>
            <div className={text ? s.reviewItemValue : `${s.reviewItemValue} ${s.reviewItemEmpty}`}>
                {text || 'Not provided'}
            </div>
            {hint}
        </div>
    );
};

// Review 页 SSN：默认打码到后 4 位，Show/Hide 链接切换明文（对齐原型 .cred-ssn-review）
const SsnValue = ({ ssn }: { ssn: string }) => {
    const [revealed, setRevealed] = React.useState(false);
    const masked = `•••-••-${ssn.replace(/\D/g, '').slice(-4)}`;
    return (
        <span className={s.ssnReview}>
            <span>{revealed ? ssn : masked}</span>
            <button
                type="button"
                className={s.ssnReviewToggle}
                aria-label={revealed ? 'Hide SSN' : 'Show SSN'}
                onClick={() => setRevealed((r) => !r)}
            >
                {revealed ? 'Hide' : 'Show'}
            </button>
        </span>
    );
};

const ViewGrid = ({ items }: { items: Item[] }) => (
    <div className={s.reviewRow}>
        {items.map((it) => (
            <ViewItem key={it.label} {...it} />
        ))}
    </div>
);

const isImageUrl = (u?: string) => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(u || '');

// 私域上传保存的是资源 key（非可访问 URL），预览/打开前需换取签名 URL；老数据可能已是 http URL，直接用
const ReviewDoc = ({ label, url }: { label: string; url?: string }) => {
    const [signedUrl, setSignedUrl] = React.useState<string | undefined>(url && url.includes('http') ? url : undefined);

    React.useEffect(() => {
        let alive = true;
        if (!url || url.includes('http')) {
            setSignedUrl(url && url.includes('http') ? url : undefined);
            return undefined;
        }
        getPrivateFilePreview(url)
            .then((res: any) => {
                if (!alive) return;
                setSignedUrl(res?.data?.data?.signedUrl || res?.data?.data?.singedUrl);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [url]);

    // 图片判定基于原始 key 的扩展名（签名 URL 常带 query，不可靠）
    const isImage = isImageUrl(url);
    return (
        <div className={s.reviewDoc}>
            <div className={s.reviewDocThumb}>
                {isImage && signedUrl ? (
                    <img src={signedUrl} alt={label} />
                ) : (
                    <span className={s.reviewDocPdf}>
                        {isImage ? 'IMG' : <img src={PdfIcon} alt="PDF" className={s.reviewDocPdfIcon} />}
                    </span>
                )}
            </div>
            {signedUrl && (
                <a href={signedUrl} target="_blank" rel="noopener noreferrer" className={s.reviewDocView}>
                    View
                </a>
            )}
        </div>
    );
};

// Review 页展示已上传文件（对齐原型 cred-review-row-docs）：图片显示缩略图，PDF 显示占位，均可 View 打开
const ReviewDocs = ({ title, docs }: { title: string; docs: { label: string; url?: string }[] }) => {
    const present = docs.filter((d) => !isBlank(d.url));
    if (!present.length) return null;
    return (
        <div className={s.reviewDocsGroup}>
            <div className={s.reviewItemLabel} style={{ marginBottom: 8 }}>
                {title}
            </div>
            <div className={s.reviewDocsRow}>
                {present.map((d) => (
                    <ReviewDoc key={d.label} label={d.label} url={d.url} />
                ))}
            </div>
        </div>
    );
};

// identification.type 存的是 dict_key（driver-license…），只读展示时按 setting type=IDENTITY_TYPE 映射回可读 label
const IdentityTypeValue = ({ value }: { value?: string }) => {
    const [setting] = useSettings();
    const options = setting[EnumFields.IDENTITY_TYPE] || [];
    return <>{options.find((o) => o.value === value)?.label ?? value ?? ''}</>;
};

function renderIdentity(v: Values) {
    const c = v.contactAddress || {};
    const m = v.mailingAddress || {};
    const items: Item[] = [
        { label: 'First name', value: v.firstName },
        { label: 'Middle name', value: v.middleName, optional: true },
        { label: 'Last name', value: v.lastName },
        { label: 'Former name or alias', value: v.formerName, optional: true },
        { label: 'Date of birth', value: v.birthDate },
        { label: 'Gender', value: v.gender },
        { label: 'Individual NPI (Type 1)', value: v.npi },
        { label: 'Credentials', value: Array.isArray(v.credential) ? v.credential.join(', ') : v.credential },
        { label: 'Citizenship or nationality', value: v.citizenship },
    ];
    if (v.citizenship && v.citizenship !== 'US citizen') {
        items.push({ label: 'Work authorization type', value: v.workAuthorizationType });
    }
    items.push(
        { label: 'Primary work email', value: v.email },
        { label: 'Mobile phone', value: fmtPhone(v.tel) },
        { label: 'Fax', value: fmtPhone(v.fax) },
        { label: 'Address', value: c.practiceAddress },
        { label: 'City', value: c.practiceCity },
        { label: 'State', value: c.practiceState },
        { label: 'ZIP code', value: c.practiceZip },
    );
    if (m.practiceAddress || m.practiceCity || m.practiceState || m.practiceZip) {
        items.push(
            { label: 'Mailing address', value: m.practiceAddress },
            { label: 'Mailing city', value: m.practiceCity },
            { label: 'Mailing state', value: m.practiceState },
            { label: 'Mailing ZIP code', value: m.practiceZip },
        );
    }
    return <ViewGrid items={items} />;
}

function renderLicenses(v: Values) {
    // practice license 现在整体选填，表单默认的空行不展示，否则 review 会多出一个全是 Not provided 的「License 1」
    const licenses: any[] = (v.practiceLicenses || []).filter(hasEntryValue);
    const boards: any[] = v.boardCertifications || [];
    // 选填的 DEA / CDS / multistate 之前没有在 review 展示，导致已填信息在 review 页「消失」
    const deas: any[] = (v.deaLicenses || []).filter(hasEntryValue);
    const cds = hasEntryValue(v.cdsCertificate) ? v.cdsCertificate : null;
    const compacts: string[] = (v.multistateAuthorities || []).filter(Boolean);
    return (
        <>
            {licenses.map((lic, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`lic-${i}`} className={s.reviewSubGroup}>
                    <div className={s.reviewSubTitle}>License {i + 1}</div>
                    <ViewGrid
                        items={[
                            { label: 'License state', value: lic?.state },
                            { label: 'License type', value: lic?.type },
                            { label: 'License number', value: lic?.licenseNumber },
                            { label: 'Issue date', value: lic?.effectiveDate },
                            { label: 'Expiration date', value: lic?.expireDate },
                        ]}
                    />
                </div>
            ))}
            <ViewGrid
                items={[
                    {
                        label: 'ID type',
                        value: v.identification?.type ? <IdentityTypeValue value={v.identification.type} /> : undefined,
                    },
                ]}
            />
            {boards
                .filter((b) => b?.boardBody)
                .map((b, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`board-${i}`} className={s.reviewSubGroup}>
                        <div className={s.reviewSubTitle}>Board certification {i + 1}</div>
                        <ViewGrid
                            items={[
                                {
                                    label: 'Certifying body',
                                    value: b?.boardBody,
                                },
                                { label: 'Certificate number', value: b?.licenseNumber },
                                { label: 'Certification expiry', value: b?.expireDate },
                            ]}
                        />
                    </div>
                ))}
            {deas.map((dea, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`dea-${i}`} className={s.reviewSubGroup}>
                    <div className={s.reviewSubTitle}>DEA license {i + 1}</div>
                    <ViewGrid
                        items={[
                            { label: 'License state', value: dea?.state, optional: true },
                            { label: 'DEA license number', value: dea?.licenseNumber, optional: true },
                            { label: 'Expiration date', value: dea?.expireDate, optional: true },
                        ]}
                    />
                </div>
            ))}
            {cds && (
                <div className={s.reviewSubGroup}>
                    <div className={s.reviewSubTitle}>State controlled substance (CDS) certificate</div>
                    <ViewGrid
                        items={[
                            { label: 'CDS state', value: cds.state, optional: true },
                            { label: 'CDS certificate number', value: cds.licenseNumber, optional: true },
                            { label: 'CDS expiration date', value: cds.expireDate, optional: true },
                        ]}
                    />
                </div>
            )}
            {!!compacts.length && (
                <div className={s.reviewSubGroup}>
                    <div className={s.reviewSubTitle}>Multistate authority</div>
                    <ViewGrid items={[{ label: 'Compacts', value: compacts, optional: true }]} />
                </div>
            )}
            <ReviewDocs
                title="Uploaded documents"
                docs={[
                    { label: 'ID front', url: v.identification?.frontUrl },
                    { label: 'ID back', url: v.identification?.backUrl },
                    ...licenses.map((lic, i) => ({ label: `License ${i + 1} copy`, url: lic?.url })),
                    ...boards
                        .filter((b) => b?.boardBody)
                        .map((b, i) => ({ label: `Board cert ${i + 1}`, url: b?.url })),
                    ...deas.map((dea, i) => ({ label: `DEA license ${i + 1} picture`, url: dea?.url })),
                ]}
            />
        </>
    );
}

// ehrSystems 存的是 dict_key，只读展示时按 setting type=EHR_TYPE 映射回可读 label
// 选中 Other 时用 ehrSystemsOther 自定义名称替换 "Other" 展示
const EhrSystemsValue = ({ value, other }: { value?: string[]; other?: string }) => {
    const [setting] = useSettings();
    const options = setting[EnumFields.EHR_TYPE] || [];
    const labels = (value || []).map((key) => {
        const label = options.find((o) => o.value === key)?.label ?? key;
        if (String(label).trim().toLowerCase() === 'other' && other) return `Other: ${other}`;
        return label;
    });
    return <>{labels.join(', ')}</>;
};

// 执业地址查看态：一个州一条，用州 chips 切换（对齐 Settings 原型）。
// 没选州的行（草稿）也要能看到，chip 上显示占位序号。
const PracticeAddressView = ({ items }: { items: any[] }) => {
    const [active, setActive] = React.useState(0);
    const idx = active < items.length ? active : 0;
    const a = items[idx] || {};
    return (
        <div className={`${s.reviewSubGroup} ${s.reviewAddrBlock}`}>
            <div className={s.reviewSubTitle}>Practice address</div>
            {items.length > 1 && (
                <div className={s.reviewChips}>
                    {items.map((it, i) => (
                        <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={`addr-chip-${i}`}
                            type="button"
                            className={i === idx ? `${s.reviewChip} ${s.reviewChipActive}` : s.reviewChip}
                            onClick={() => setActive(i)}
                        >
                            {stateName(it?.practiceState) || `Address ${i + 1}`}
                        </button>
                    ))}
                </div>
            )}
            <ViewGrid
                items={[
                    { label: 'Address', value: a.practiceAddress },
                    { label: 'City', value: a.practiceCity },
                    // 只有一条地址时没有 chips，州要单独列出来，否则这条信息在查看态里看不到
                    ...(items.length > 1 ? [] : [{ label: 'State', value: stateName(a.practiceState) }]),
                    { label: 'ZIP code', value: a.practiceZip },
                    { label: 'Services provided at this location', value: locationCareDeliveryLabel(a.careDelivery) },
                ]}
            />
        </div>
    );
};

const optionLabel = (options: { value: string; label: string }[], v?: string) =>
    options.find((o) => o.value === v)?.label ?? v;

// D0（10/26）：选 payer 前那道"是否已 credential 过"的答案（prior-credentialing 接口，不在 Practice 里），
// 答过才显示（原型 cred-npi-history 的 when）。priorNoReason 存的是选项文案（solo）或 code（group），按关键词兼容
const priorCredentialedText = (dto: any): string | null => {
    if (!dto || dto.priorCredentialed == null) return null;
    if (dto.priorCredentialed) return 'Yes, under my own NPI directly with payers';
    const r = String(dto.priorNoReason || '');
    if (/company|headway|VIA_COMPANY/i.test(r))
        return 'Through a company like Headway, Alma, Grow Therapy or SonderMind';
    if (/hospital|VIA_HOSPITAL/i.test(r)) return 'With a hospital or group practice';
    return 'Never credentialed';
};

const CredentialedBeforeItem = () => {
    const providerId = useCredTargetProvider();
    const [text, setText] = React.useState<string | null>(null);
    React.useEffect(() => {
        let alive = true;
        getProviderPriorCredentialing(providerId)
            .then((res: any) => alive && setText(priorCredentialedText(res?.error ? null : res?.data?.data)))
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [providerId]);
    return text ? <ViewItem label="Credentialed before" value={text} /> : null;
};

function renderPractice(v: Values) {
    const ehr: string[] = Array.isArray(v.ehrSystems) ? v.ehrSystems.filter(Boolean) : [];
    const addrs: any[] = Array.isArray(v[ADDR]) && v[ADDR].length ? v[ADDR] : [{}];
    return (
        <>
            <p className={s.reviewHint}>
                The address each payer files under per state is in Payer requirements, under Service address by state.
            </p>
            <div className={s.reviewRow}>
                {[
                    { label: 'Clinic / Practice name', value: v.practiceName },
                    {
                        label: 'Practice start',
                        value: v.startDate ? dayjs(v.startDate).format('MM/YYYY') : undefined,
                    },
                    { label: 'Visits billed a month', value: optionLabel(MONTHLY_VISITS_OPTIONS, v.monthlyVisits) },
                    { label: 'How patients pay', value: optionLabel(PATIENT_PAYER_MIX_OPTIONS, v.patientPayerMix) },
                ].map((it) => (
                    <ViewItem key={it.label} {...it} />
                ))}
                <CredentialedBeforeItem />
                {[
                    { label: 'Practice phone', value: fmtPhone(v.practicePhone) },
                    { label: 'Practice type', value: v.practiceType },
                    // 尚未提交时表单里存的是 "Others" + 单独的输入内容，展示上合成一条（提交后回填的就是实际专科）
                    {
                        label: 'Specialty offered',
                        value: (v.specialtyOffered || []).map((sp: string) =>
                            sp === SPECIALTY_OTHERS && v.specialtyOfferedOther
                                ? `${SPECIALTY_OTHERS}: ${v.specialtyOfferedOther}`
                                : sp,
                        ),
                    },
                    { label: 'Care delivery', value: v.careDelivery },
                    {
                        label: 'EHR',
                        value: ehr.length ? <EhrSystemsValue value={ehr} other={v.ehrSystemsOther} /> : undefined,
                    },
                ].map((it) => (
                    <ViewItem key={it.label} {...it} />
                ))}
            </div>
            <PracticeAddressView items={addrs} />
        </>
    );
}

const centsToDollars = (c: any): string | undefined =>
    c == null || c === '' ? undefined : `$${(Number(c) / 100).toLocaleString('en-US')}`;

function renderMalpractice(v: Values) {
    const { carrier } = v;
    return (
        <>
            <ViewGrid
                items={[
                    { label: 'Insurance carrier', value: carrier },
                    { label: 'Policy / certificate number', value: v.licenseNumber },
                    { label: 'Effective date', value: v.effectiveDate },
                    { label: 'Expiration date', value: v.expireDate },
                    { label: 'Per-occurrence limit', value: centsToDollars(v.perOccurrenceCents) },
                    { label: 'Aggregate limit', value: centsToDollars(v.aggregateCents) },
                ]}
            />
            <MalpracticeExpiryNotice expireDate={v.expireDate} className={s.malpracticeWarnView} />
            <ReviewDocs title="Certificate of insurance" docs={[{ label: 'COI', url: v.url }]} />
        </>
    );
}

function renderExperience(v: Values) {
    const work: any[] = (v.providerExperienceList || []).filter((w: any) => w?.organizationName || w?.title);
    const edu: any[] = (v.providerEducationList || []).filter((e: any) => e?.schoolName);
    return (
        <>
            {work.length ? (
                work.map((w, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`w-${i}`} className={s.reviewSubGroup}>
                        <div className={s.reviewItemValue}>{w?.title || 'Untitled role'}</div>
                        <div className={s.reviewItemLabel}>
                            {[
                                w?.organizationName || 'Unknown organization',
                                fmt(w?.startDate),
                                w?.currently ? 'Present' : fmt(w?.endDate),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </div>
                    </div>
                ))
            ) : (
                <ViewGrid items={[{ label: 'Work experience', value: undefined }]} />
            )}
            {edu.length ? (
                edu.map((e, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`e-${i}`} className={s.reviewSubGroup}>
                        <div className={s.reviewItemValue}>
                            {[e?.degree, e?.schoolName].filter(Boolean).join(' at ')}
                        </div>
                        <div className={s.reviewItemLabel}>{e?.major || ''}</div>
                    </div>
                ))
            ) : (
                <ViewGrid items={[{ label: 'Education', value: undefined }]} />
            )}
        </>
    );
}

function renderReferences(v: Values) {
    const refs: any[] = (v.references || []).filter((r: any) => r?.fullName);
    if (!refs.length) return <ViewGrid items={[{ label: 'References', value: undefined }]} />;
    return (
        <>
            {refs.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`ref-${i}`} className={s.reviewSubGroup}>
                    <div className={s.reviewSubTitle}>{[r?.fullName, r?.credentials].filter(Boolean).join(', ')}</div>
                    <ViewGrid
                        items={[
                            {
                                label: 'Relationship',
                                value: r?.relationship === 'Other' ? r?.relationshipOther || 'Other' : r?.relationship,
                            },
                            { label: 'Organization', value: r?.organization },
                            { label: 'Phone', value: fmtPhone(r?.phone) },
                            { label: 'Email', value: r?.email },
                        ]}
                    />
                </div>
            ))}
        </>
    );
}

function renderFinal(v: Values) {
    const ssn: string = v.ssn || '';
    const items: Item[] = [{ label: 'CAQH account', value: v.hasCaqhAccount }];
    if (v.hasCaqhAccount === true) {
        items.push({ label: 'CAQH username', value: v.caqhUsername });
        // 10/15：上次 CAQH 检查的结果（没查过不显示）；不是「能登录且新」的标红
        const caqh = caqhStatusLine(v.caqhCheck);
        if (caqh)
            items.push({
                label: 'CAQH login',
                value: caqh.ok ? caqh.text : <span style={{ color: '#B91C1C' }}>{caqh.text}</span>,
            });
    }
    items.push(
        { label: 'Social security number', value: ssn ? <SsnValue ssn={ssn} /> : '' },
        { label: 'Legal tax name', value: v.legalTaxName },
        { label: 'Tax ID / EIN', value: v.taxId },
        {
            label: 'Business entity type',
            value: v.businessEntityType,
        },
    );
    items.push({ label: 'Admitting privileges at a facility', value: v.hasFacilityPrivileges });
    if (v.hasFacilityPrivileges === true) {
        items.push({ label: 'Facility name', value: v.facilityName });
    }
    const disclosures: any[] = v.disclosures || [];
    return (
        <>
            <ViewGrid items={items} />
            {v.hasCaqhAccount === false && (
                <div className={s.caqhAlert}>
                    <svg className={s.caqhAlertIcon} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <div className={s.caqhAlertBody}>
                        <p className={s.caqhAlertText}>
                            You don&apos;t have a CAQH profile yet. Payers need it to verify your credentials, so please
                            create one. You can submit now and set it up after.
                        </p>
                        <a
                            className={s.caqhAlertLink}
                            href="https://proview.caqh.org/PR/Registration"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Register with CAQH
                            <ExternalLinkIcon />
                        </a>
                    </div>
                </div>
            )}
            <ReviewDocs title="Uploaded W-9 form" docs={[{ label: 'W-9', url: v.w9FormUrl }]} />
            <div className={s.reviewSubGroup} style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #F3F4F6' }}>
                <div className={s.reviewItemLabel} style={{ marginBottom: 8 }}>
                    Disclosures
                </div>
                {DISCLOSURES.map((d, i) => {
                    const a = disclosures[i]?.answer;
                    let disp = 'Not answered';
                    if (a === true) disp = 'Yes';
                    else if (a === false) disp = 'No';
                    return (
                        <div key={d.id} className={s.reviewItem} style={{ marginBottom: 8 }}>
                            <div className={s.reviewItemLabel}>{d.q}</div>
                            <div
                                className={
                                    a === undefined || a === null
                                        ? `${s.reviewItemValue} ${s.reviewItemEmpty}`
                                        : s.reviewItemValue
                                }
                            >
                                {disp}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

export function renderSectionView(key: CredStepKey, values: Values) {
    switch (key) {
        case 'identity':
            return renderIdentity(values);
        case 'licenses':
            return renderLicenses(values);
        case 'practice':
            return renderPractice(values);
        case 'malpractice':
            return renderMalpractice(values);
        case 'experience':
            return renderExperience(values);
        case 'references':
            return renderReferences(values);
        case 'final':
            return renderFinal(values);
        default:
            return null;
    }
}
